import { authClient } from "@/lib/authClient";
import { RouteTable } from "@/lib/routes";
import { trpc } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@repo/ui/components/Button";
import { Card, CardContent } from "@repo/ui/components/Card";
import { FieldGroup } from "@repo/ui/components/Field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/Form";
import { Input } from "@repo/ui/components/Input";
import { LoadingWrapper } from "@repo/ui/components/LoadingWrapper";
import { toast } from "@repo/ui/lib/toast";
import { useMutation } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { setPasswordSchema, type SetPasswordFormData } from "./formSchema";

const defaultValues: SetPasswordFormData = {
  currentPassword: "",
  password: "",
  confirmPassword: "",
};

export const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: session, refetch: refetchSession } = authClient.useSession();
  const clearPasswordFlagMutation = trpc.user.clearPasswordChangeFlag.useMutation();

  const form = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues,
  });

  const { mutate: changePassword, isPending } = useMutation({
    mutationFn: async ({ currentPassword, password }: SetPasswordFormData) => {
      const response = await authClient.changePassword({
        currentPassword,
        newPassword: password,
        revokeOtherSessions: false,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to change password");
      }

      return response;
    },
    onSuccess: async () => {
      await clearPasswordFlagMutation.mutateAsync();
      await refetchSession();

      toast.success("Password updated successfully");

      const callbackUrl = searchParams.get("callbackUrl") || RouteTable.dashboard();
      void navigate(callbackUrl);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (!session) {
    return <Navigate to={RouteTable.login()} />;
  }

  return (
    <div className="bg-muted flex min-h-svh items-center justify-center p-6 md:p-10">
      <div className="flex w-full max-w-lg flex-col gap-6">
        <Card className="overflow-hidden p-0">
          <LoadingWrapper isLoading={isPending} overlayClassName="rounded-xl">
            <CardContent className="p-8">
              <Form {...form}>
                <form
                  noValidate
                  onSubmit={(e) => void form.handleSubmit((data) => changePassword(data))(e)}
                >
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <h1 className="text-primary text-2xl font-bold">Set Your Password</h1>
                      <p className="text-muted-foreground text-balance">
                        Please set a new password for your account
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      name="currentPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Temporary Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Enter temporary password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Create a strong password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password *</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Re-enter the new password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" color="primary" disabled={form.formState.isSubmitting}>
                      {form.formState.isSubmitting && (
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Set Password
                    </Button>
                  </FieldGroup>
                </form>
              </Form>
            </CardContent>
          </LoadingWrapper>
        </Card>
      </div>
    </div>
  );
};
